import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Volume2, RotateCcw, Star } from 'lucide-react';
import { Word } from '@/data/words';

interface WordCardProps {
  word: Word;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalWords: number;
}

export function WordCard({ word, onNext, onPrevious, currentIndex, totalWords }: WordCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showStar, setShowStar] = useState(false);

  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word.word);
      utterance.rate = 0.8;
      utterance.pitch = 1.2;
      speechSynthesis.speak(utterance);
      
      setShowStar(true);
      setTimeout(() => setShowStar(false), 1000);
    }
  };

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
    if (!isFlipped) {
      handleSpeak();
    }
  };

  return (
    <div className="flex flex-col items-center space-y-6 p-4">
      {/* Progress indicator */}
      <div className="flex items-center space-x-2 text-lg font-semibold text-primary">
        <span>{currentIndex + 1}</span>
        <span>/</span>
        <span>{totalWords}</span>
      </div>

      {/* Word card */}
      <Card 
        className="word-card relative w-80 h-96 cursor-pointer overflow-hidden"
        onClick={handleCardClick}
      >
        <CardContent className="p-0 h-full relative">
          {/* Front side - Image */}
          <div className={`absolute inset-0 transition-transform duration-500 ${isFlipped ? 'rotate-y-180' : ''}`}>
            <img 
              src={word.image} 
              alt={word.word}
              className="w-full h-2/3 object-cover rounded-t-2xl"
            />
            <div className="p-6 h-1/3 flex items-center justify-center bg-gradient-to-r from-primary/10 to-accent/10">
              <p className="text-center text-gray-600 text-lg">
                Tap to see the word!
              </p>
            </div>
          </div>

          {/* Back side - Word */}
          <div className={`absolute inset-0 transition-transform duration-500 ${isFlipped ? '' : 'rotate-y-180'} bg-gradient-to-br from-primary to-accent flex flex-col items-center justify-center text-white`}>
            <h2 className="text-6xl font-bold mb-4 rainbow-text">{word.word}</h2>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSpeak();
              }}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              size="lg"
            >
              <Volume2 className="w-6 h-6 mr-2" />
              Listen
            </Button>
          </div>

          {/* Star animation */}
          {showStar && (
            <div className="absolute top-4 right-4 animate-bounce">
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      <div className="flex space-x-4">
        <Button
          onClick={onPrevious}
          disabled={currentIndex === 0}
          className="btn-fun"
          size="lg"
        >
          ← Previous
        </Button>
        
        <Button
          onClick={() => setIsFlipped(!isFlipped)}
          variant="outline"
          size="lg"
          className="border-2 border-primary text-primary hover:bg-primary hover:text-white"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Flip
        </Button>
        
        <Button
          onClick={onNext}
          disabled={currentIndex === totalWords - 1}
          className="btn-fun"
          size="lg"
        >
          Next →
        </Button>
      </div>
    </div>
  );
}